/**
 * HospitalAssignmentSettingsPage — configure auto hospital assignment per employment role.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import {
  useEmploymentRoles,
  useUpdateEmploymentRole,
  useEmploymentRoleHospitalSubset,
  useUpdateHospitalSubset,
  useSyncHospitals,
} from '../hooks';
import { HospitalAssignmentField } from '@/features/users/components/HospitalAssignmentField';
import { localizedName } from '@/utils/localized';
import toast from 'react-hot-toast';
import type { EmploymentRole } from '../types';

const MODES = ['none', 'all', 'all_active', 'subset'] as const;

function ModeLabel({ mode, t }: { mode: string; t: (key: string) => string }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    none: { label: t('hospitalAssignment.none'), color: ADMIN_COLORS.textSecondary, bg: ADMIN_COLORS.surface },
    all: { label: t('hospitalAssignment.all'), color: ADMIN_COLORS.primaryText, bg: ADMIN_COLORS.primarySoft },
    all_active: { label: t('hospitalAssignment.allActive'), color: ADMIN_COLORS.forestText, bg: ADMIN_COLORS.forestSoft },
    subset: { label: t('hospitalAssignment.subset'), color: ADMIN_COLORS.warningText, bg: ADMIN_COLORS.warningBg },
  };
  const m = map[mode] || map.none;
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-bold"
      style={{ color: m.color, backgroundColor: m.bg }}
    >
      {m.label}
    </span>
  );
}

function RoleCard({ role }: { role: EmploymentRole }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [selectedMode, setSelectedMode] = useState(role.hospitalAssignMode || 'none');
  const [subsetIds, setSubsetIds] = useState<string[]>([]);
  const [subsetLoaded, setSubsetLoaded] = useState(false);

  const updateRole = useUpdateEmploymentRole();
  const updateSubset = useUpdateHospitalSubset();
  const syncMutation = useSyncHospitals();

  const { data: hospitalSubset, isLoading: subsetLoading } = useEmploymentRoleHospitalSubset(
    expanded && selectedMode === 'subset' ? role.id : undefined,
  );

  // Initialize subset IDs once data loads
  if (hospitalSubset && !subsetLoaded) {
    setSubsetIds(hospitalSubset.map((h) => h.hospitalId));
    setSubsetLoaded(true);
  }

  const handleModeChange = async (mode: string) => {
    setSelectedMode(mode);
    triggerHapticFeedback();
    try {
      await updateRole.mutateAsync({ id: role.id, data: { hospitalAssignMode: mode } });
      toast.success(t('hospitalAssignment.modeChanged'));
      if (mode === 'subset') {
        setSubsetLoaded(false);
      }
    } catch {
      toast.error(t('users.updateFailed'));
      setSelectedMode(role.hospitalAssignMode || 'none');
    }
  };

  const handleSaveSubset = async () => {
    triggerHapticFeedback();
    try {
      await updateSubset.mutateAsync({ roleId: role.id, hospitalIds: subsetIds });
      toast.success(t('hospitalAssignment.subsetSaved'));
    } catch {
      toast.error(t('users.updateFailed'));
    }
  };

  const handleSync = async () => {
    triggerHapticFeedback();
    try {
      await syncMutation.mutateAsync(role.id);
      toast.success(t('hospitalAssignment.synced'));
    } catch {
      toast.error(t('hospitalAssignment.syncFailed'));
    }
  };

  const userCount = role._count?.users ?? 0;

  return (
    <motion.div variants={staggerItem}>
      <div
        className="rounded-xl overflow-hidden"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        {/* Header */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            setExpanded(!expanded);
            if (!expanded) setSubsetLoaded(false);
          }}
          className="w-full flex items-center justify-between p-3 md:p-4 cursor-pointer hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: ADMIN_COLORS.primarySoft }}
            >
              <Briefcase className="w-5 h-5" style={{ color: ADMIN_COLORS.primaryText }} />
            </div>
            <div className="flex-1 min-w-0 text-start">
              <p className="font-bold text-sm truncate" style={{ color: ADMIN_COLORS.navy }}>
                {localizedName(role, i18n.language)}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <ModeLabel mode={selectedMode} t={t} />
                <span className="flex items-center gap-1 text-xs" style={{ color: ADMIN_COLORS.textSecondary }}>
                  <Users className="w-3 h-3" />
                  {userCount}
                </span>
              </div>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: ADMIN_COLORS.textSecondary }} />
          ) : (
            <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: ADMIN_COLORS.textSecondary }} />
          )}
        </button>

        {/* Expanded details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div
                className="px-3 pb-3 md:px-4 md:pb-4 space-y-3"
                style={{ borderTop: `1px solid ${ADMIN_COLORS.border}` }}
              >
                {/* Mode selector */}
                <div className="pt-3">
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: ADMIN_COLORS.textSecondary }}>
                    {t('hospitalAssignment.mode')}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {MODES.map((mode) => (
                      <button
                        key={mode}
                        onClick={() => handleModeChange(mode)}
                        disabled={updateRole.isPending}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                        style={{
                          backgroundColor: selectedMode === mode ? ADMIN_COLORS.primarySoft : ADMIN_COLORS.ice,
                          color: selectedMode === mode ? ADMIN_COLORS.primaryText : ADMIN_COLORS.textSecondary,
                          border: `1px solid ${selectedMode === mode ? ADMIN_COLORS.primary : ADMIN_COLORS.border}`,
                        }}
                      >
                        {t(`hospitalAssignment.${mode === 'all_active' ? 'allActive' : mode}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subset picker */}
                {selectedMode === 'subset' && (
                  <div>
                    <label className="text-xs font-bold mb-1.5 block" style={{ color: ADMIN_COLORS.textSecondary }}>
                      {t('hospitalAssignment.selectHospitals')}
                    </label>
                    {subsetLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
                      </div>
                    ) : (
                      <>
                        <HospitalAssignmentField
                          selectedIds={subsetIds}
                          primaryId={undefined}
                          onChange={(ids) => setSubsetIds(ids)}
                        />
                        <button
                          onClick={handleSaveSubset}
                          disabled={updateSubset.isPending}
                          className="mt-2 px-4 py-1.5 rounded-lg text-xs font-bold text-white transition-colors cursor-pointer disabled:opacity-50"
                          style={{ backgroundColor: ADMIN_COLORS.primary }}
                        >
                          {updateSubset.isPending ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            t('common.save')
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Sync button */}
                {selectedMode !== 'none' && (
                  <button
                    onClick={handleSync}
                    disabled={syncMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
                    style={{
                      backgroundColor: ADMIN_COLORS.forestSoft,
                      color: ADMIN_COLORS.forestText,
                      border: `1px solid ${ADMIN_COLORS.border}`,
                    }}
                  >
                    {syncMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    {t('hospitalAssignment.syncNow')}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export function HospitalAssignmentSettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useEmploymentRoles({ limit: 100 });

  const roles = data?.employmentRoles ?? [];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg md:text-xl font-bold" style={{ color: ADMIN_COLORS.navy }}>
          {t('hospitalAssignment.title')}
        </h1>
      </div>

      {/* Role list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
        </div>
      ) : roles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
            {t('hospitalAssignment.noRoles')}
          </p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="space-y-2"
        >
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
